import { useAuth } from "context/AuthContext";
import { useTooltip } from "context/TooltipProvider";
import { useTranslation } from "react-i18next";
import React from "react";
import { addInstructionFeedback } from "services/doubtfulService";
import { DoubtfulCase, DoubtfulCaseFeedback, Roles } from "types";
import { feedbackIcon } from "utils/constant";
import { errorHandlerOnForm, hasRole } from "utils/helper";

type Props = {
  instruction: DoubtfulCase;
  instruction_feedbacks: DoubtfulCaseFeedback[];
  setRefetch: React.Dispatch<React.SetStateAction<boolean>>;
};

const DoubtfulFeedbackSection: React.FC<Props> = ({
  instruction,
  instruction_feedbacks,
  setRefetch,
}) => {
  const { t } = useTranslation();

  const statusLabels: Record<string, string> = {
    Pending: t("pending"),
    Approved: t("approved"),
    Rejected: t("rejected"),
    Respond_Back: t("respond_back"),
  };

  const statusColors: Record<string, string> = {
    Pending: "bg-gray-400",
    Approved: "bg-green-500",
    Rejected: "bg-red-500",
    Respond_Back: "bg-yellow-500",
  };

  const getStatusLabel = (status?: string | null) => {
    if (!status) return "";
    return statusLabels[status] || status;
  };

  const getStatusColor = (status?: string | null) => {
    if (!status) return "bg-gray-400";
    return statusColors[status] || "bg-gray-400";
  };

  const { loginData } = useAuth();
  const { showTooltip } = useTooltip();
  const isCoordinator = hasRole(loginData?.roles, [Roles.coordinator]);
  // const isDirector = hasRole(loginData?.roles, [Roles.director]);

  const [newComment, setNewComment] = React.useState<string>("");

  return (
    <div>
      <h1 className="text-black font-semibold text-lg flex flex-row gap-2 items-center mb-2">
        <span className="text-[#AA232F] text-xl">{feedbackIcon}</span>
        <span className="tracking-wide">{t("feedback_section_title")}</span>
      </h1>
      <div className="flex flex-col gap-4 mt-2 justify-between h-[70vh] w-full">
        <div className="flex flex-col overflow-y-auto gap-3 pr-1">
          {instruction_feedbacks && instruction_feedbacks.length > 0 ? (
            instruction_feedbacks?.map((feedback, index) => (
              <div
                key={index}
                className={`flex flex-row items-start gap-3 rounded-lg shadow-sm border border-gray-200 px-4 py-3 ${
                  index % 2 === 0 ? "bg-[#F7F7F7]" : "bg-white"
                }`}
                style={{ overflowWrap: "break-word" }}
              >
                {/* Feedback type icon */}
                <div className="flex-1">
                  <div className="flex flex-row gap-2 items-center mb-1">
                    <span className="text-xs text-gray-800"></span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-semibold text-white ${getStatusColor(
                        feedback.status,
                      )}`}
                    >
                      {getStatusLabel(feedback.status)}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-1">
                    {feedback?.feedback_text}
                  </p>
                  <span className="text-[10px] text-gray-500 mt-1 capitalize">
                    {feedback.feedback_type}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center text-gray-800 py-8">
              {t("no_feedback_yet")}
            </div>
          )}
        </div>

        {isCoordinator &&
          instruction?.request_status === "forwarded_to_cordinator" && (
            <div
              style={{ boxShadow: "rgb(217 217 217 / 90%) 0px -6px 7px 0px" }}
              className="px-6 py-4 w-full bg-white rounded-lg border border-gray-200 mt-2"
            >
              <p className="font-semibold text-[#AA232F] mb-2">
                {/* {statusLabels["respond_back"]} */}
              </p>
              <div className="flex flex-col mt-2">
                <textarea
                  rows={4}
                  className="w-full bg-[#F2F2F2] px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#AA232F] text-sm resize-none"
                  placeholder={t("write_your_feedback_here")}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                />
                <div className="text-right mt-2">
                  <button
                    className="px-5 py-2 rounded-full bg-[#AA232F] text-white text-xs font-semibold shadow hover:bg-[#E90101] transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-[#AA232F]"
                    onClick={() => {
                      if (!newComment.trim()) {
                        showTooltip(t("please_enter_a_comment"), "error");
                        return;
                      }

                      addInstructionFeedback({
                        if_for_asking: instruction?.team_final_instruction_date
                          ? true
                          : false,
                        feedback_type:
                          instruction?.team_draft_date ||
                          instruction?.team_resolution_summary_date
                            ? "TeamMemberAsking"
                            : instruction?.team_final_instruction_date
                              ? "TeamMemberGiving"
                              : "Prosecutor",
                        feedback_text: newComment,
                        status: "Pending",
                        instruction: instruction?.id || null,
                      })
                        .then(() => {
                          setNewComment("");
                          setRefetch((prev: any) => !prev);
                          showTooltip(
                            t("comment_added_successfully"),
                            "success",
                          );
                        })
                        .catch((error) => {
                          showTooltip(
                            errorHandlerOnForm(error) ||
                              t("error_adding_comment"),
                            "error",
                          );
                        });
                    }}
                  >
                    {t("send")}
                  </button>
                </div>
              </div>
            </div>
          )}
      </div>
    </div>
  );
};

export default DoubtfulFeedbackSection;
